import { create } from 'zustand';
import { getErrorMessage } from '@/shared/lib/errors';
import { RequestStatus } from '@/shared/model/requestStatus';
import { cityService } from '../api/cityService';
import type { CityData, CityState } from './types';

let listRequestId = 0;
let detailsRequestId = 0;

const initialState: CityData = {
    cities: [],
    city: null,
    listStatus: RequestStatus.Idle,
    detailsStatus: RequestStatus.Idle,
    listError: null,
    detailsError: null,
    loading: false,
};

/** Хранит справочник и выбранный город */
export const useCities = create<CityState>((set, get) => ({
    ...initialState,

    getCities: async (force = false) => {
        const shouldReuseCityList = !force && (get().listStatus === RequestStatus.Success || get().listStatus === RequestStatus.Loading);

        if (shouldReuseCityList) return;

        const requestId = ++listRequestId;
        let status = RequestStatus.Success;

        set({
            listStatus: RequestStatus.Loading,
            listError: null,
            loading: true
        });

        try {
            const cities = await cityService.getAll();

            if (requestId === listRequestId) set({ cities });
        } catch (error: unknown) {
            status = RequestStatus.Error;

            if (requestId === listRequestId) {
                set({ listError: getErrorMessage(error, 'Не удалось загрузить города') });
            }
        } finally {
            if (requestId === listRequestId) set({
                listStatus: status,
                loading: false
            });
        }
    },

    getCityById: async (id) => {
        const requestId = ++detailsRequestId;
        let status = RequestStatus.Success;

        set({
            city: null,
            detailsStatus: RequestStatus.Loading,
            detailsError: null
        });

        try {
            const city = await cityService.getById(id);

            if (requestId === detailsRequestId) set({ city });
        } catch (error: unknown) {
            status = RequestStatus.Error;

            if (requestId === detailsRequestId) {
                set({ detailsError: getErrorMessage(error, 'Не удалось загрузить город') });
            }
        } finally {
            if (requestId === detailsRequestId) set({ detailsStatus: status });
        }
    },

    clearCity: () => {
        detailsRequestId += 1;

        set({
            city: null,
            detailsStatus: RequestStatus.Idle,
            detailsError: null
        });
    },

    clearCities: () => {
        listRequestId += 1;

        set({
            cities: [],
            listStatus: RequestStatus.Idle,
            listError: null,
            loading: false
        });
    },

    clearCityState: () => {
        listRequestId += 1;
        detailsRequestId += 1;
        set(initialState);
    },
}));
